// SPDX-License-Identifier: AGPL-3.0-or-later

import {PreloadableUserPopout} from '@app/features/channel/components/PreloadableUserPopout';
import GuildMembers from '@app/features/member/state/GuildMembers';
import type {MessageInteraction} from '@app/features/messaging/models/MessagingMessage';
import styles from '@app/features/theme/styles/Message.module.css';
import {Avatar} from '@app/features/ui/components/Avatar';
import * as NicknameUtils from '@app/features/user/utils/NicknameUtils';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type {CSSProperties} from 'react';

const USED_COMMAND_DESCRIPTOR = msg({
	message: 'used {command}',
	comment:
		'Shown in the interaction header above a slash-command bot response. {command} is the slash command pill.',
});

export const InteractionHeader = observer(
	({
		interaction,
		channelId,
		guildId,
		messageDisplayCompact,
	}: {
		interaction: MessageInteraction;
		channelId: string;
		guildId?: string;
		messageDisplayCompact: boolean;
	}) => {
		const {i18n} = useLingui();
		const resolvedGuildId = guildId;
		const user = interaction.user;
		const rawName = interaction.name || 'command';
		const commandName = rawName.startsWith('/') ? rawName : `/${rawName}`;
		const template = i18n._(USED_COMMAND_DESCRIPTOR, {command: '__COMMAND__'});
		const [prefixRaw = '', suffixRaw = ''] = template.split('__COMMAND__');
		const prefix = prefixRaw.trim();
		const suffix = suffixRaw.trim();

		return (
			<div
				className={clsx(styles.repliedMessage, messageDisplayCompact && styles.repliedMessageCompact)}
				data-message-copy-hidden="true"
				data-flx="channel.interaction-header.root"
			>
				{!messageDisplayCompact && (
					<PreloadableUserPopout
						user={user}
						isWebhook={false}
						guildId={resolvedGuildId}
						channelId={channelId}
						enableLongPressActions={true}
						data-flx="channel.interaction-header.preloadable-user-popout"
					>
						<Avatar
							user={user}
							size={16}
							className={styles.repliedAvatar}
							guildId={resolvedGuildId}
							data-user-id={user.id}
							data-guild-id={resolvedGuildId}
							data-flx="channel.interaction-header.replied-avatar"
						/>
					</PreloadableUserPopout>
				)}
				<PreloadableUserPopout
					user={user}
					isWebhook={false}
					guildId={resolvedGuildId}
					channelId={channelId}
					enableLongPressActions={true}
					longPressWrapperElement="span"
					data-flx="channel.interaction-header.preloadable-user-popout--2"
				>
					<span
						className={styles.repliedUsername}
						style={
							{
								'--replied-username-color': GuildMembers.getMember(
									resolvedGuildId ?? '',
									user.id,
								)?.getColorString(),
							} as CSSProperties
						}
						data-user-id={user.id}
						data-guild-id={resolvedGuildId}
						data-flx="channel.interaction-header.replied-username"
					>
						{NicknameUtils.getNickname(user, resolvedGuildId)}
					</span>
				</PreloadableUserPopout>
				<span
					className={styles.repliedTextContent}
					style={{marginLeft: '0.25rem', display: 'inline-flex', alignItems: 'center', gap: '0.25rem'}}
					data-flx="channel.interaction-header.text-content"
				>
					{prefix.length > 0 && (
						<span style={{color: 'var(--text-chat-muted, #949ba4)'}} data-flx="channel.interaction-header.prefix">
							{prefix}
						</span>
					)}
					<span
						style={{
							backgroundColor: 'var(--background-modifier-selected, rgba(255, 255, 255, 0.08))',
							color: 'var(--brand-experiment, #5865f2)',
							padding: '0 4px',
							borderRadius: '3px',
							fontWeight: 600,
							fontFamily: 'var(--font-code, monospace)',
							fontSize: '0.85em',
							cursor: 'default',
						}}
						data-flx="channel.interaction-header.command-pill"
					>
						{commandName}
					</span>
					{suffix.length > 0 && (
						<span style={{color: 'var(--text-chat-muted, #949ba4)'}} data-flx="channel.interaction-header.suffix">
							{suffix}
						</span>
					)}
				</span>
			</div>
		);
	},
);
